import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
// eslint-disable-next-line spire/fenced-imports
import ArrowLeft from "@crescentParts/Icons/ChevronLeft";
// eslint-disable-next-line spire/fenced-imports
import ArrowRight from "@crescentParts/Icons/ChevronRight";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: EmblaCarouselStyles;
    children?: React.ReactNode;
    slidesToScroll?: number;
    options?: EmblaOptionsType;
}

type Props = OwnProps;

export interface EmblaCarouselStyles {
    mainContainer?: InjectableCss;
    prevArrowContainer?: InjectableCss;
    prevArrowButton?: ButtonProps;
    carouselMainContainer?: InjectableCss;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    nextArrowContainer?: InjectableCss;
    nextArrowButton?: ButtonProps;
}

export const EmblaCarouselStyles: EmblaCarouselStyles = {
    mainContainer: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    prevArrowContainer: {
        css: css``,
    },
    prevArrowButton: {
        css: css`
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${getColor("text.link")};
            border-radius: 50%;
            padding: 0;
            &:disabled {
                background: rgba(121, 36, 47, 0.4);
            }
            &:focus {
                outline: none;
            }
            > span {
                top: 0 !important;
                width: 8px;
            }
        `,
    },
    carouselMainContainer: {
        css: css`
            flex: 1;
            width: calc(100% - 160px);
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
    nextArrowContainer: {
        css: css``,
    },
    nextArrowButton: {
        css: css`
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${getColor("text.link")};
            border-radius: 50%;
            padding: 0;
            &:disabled {
                background: rgba(121, 36, 47, 0.4);
            }
            &:focus {
                outline: none;
            }
            > span {
                top: 0 !important;
                width: 8px;
            }
        `,
    },
};

const EmblaCarousel = ({ extendedStyles, children, options }: Props) => {
    const [styles] = useState(() => mergeToNew(EmblaCarouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.reInit(options);
        setCanScroll();
    }, [embla]);

    return (
        <StyledWrapper {...styles.mainContainer} className="embla__container">
            <StyledWrapper {...styles.prevArrowContainer}>
                <Button
                    {...styles.prevArrowButton}
                    onClick={() => embla && embla.scrollPrev()}
                    disabled={!canScrollPrev}
                >
                    <ButtonIcon src={ArrowLeft} />
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.carouselMainContainer}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer}>{children}</StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.nextArrowContainer}>
                <Button
                    {...styles.nextArrowButton}
                    onClick={() => embla && embla.scrollNext()}
                    disabled={!canScrollNext}
                >
                    <ButtonIcon src={ArrowRight} />
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default EmblaCarousel;
